import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Search, Trash2, Mail, Phone, Calendar, MapPin, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import api from "@/services/api";

interface Inscripcion {
  _id: string;
  nombre: string;
  email: string;
  celular: string;
  createdAt: string;
  taller: {
    _id: string;
    title: string;
    price: number;
    tallerInfo?: {
      fechaEvento?: string;
      lugar?: string;
    };
  };
}

const fetchInscripciones = async (): Promise<Inscripcion[]> => {
  const res = await api.get("/talleres/inscripciones");
  return res.data.data;
};

const deleteInscripcion = async (id: string) => {
  await api.delete(`/talleres/inscripciones/${id}`);
};

const AdminTalleres = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filtroTaller, setFiltroTaller] = useState("todos");

  const { data: inscripciones = [], isLoading } = useQuery({
    queryKey: ["admin-inscripciones"],
    queryFn: fetchInscripciones,
    staleTime: 2 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInscripcion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-inscripciones"] });
      toast({ title: "Inscripción eliminada" });
    },
    onError: () => {
      toast({ title: "Error al eliminar", variant: "destructive" });
    },
  });

  // Talleres únicos para el filtro
  const talleresUnicos = Array.from(
    new Map(inscripciones.map((i) => [i.taller._id, i.taller])).values()
  );

  const filtradas = inscripciones.filter((i) => {
    const matchSearch =
      i.nombre.toLowerCase().includes(search.toLowerCase()) ||
      i.email.toLowerCase().includes(search.toLowerCase()) ||
      i.celular.includes(search);
    const matchTaller = filtroTaller === "todos" || i.taller._id === filtroTaller;
    return matchSearch && matchTaller;
  });

  const exportarCSV = () => {
    const headers = ["Nombre", "Email", "Celular", "Taller", "Fecha inscripción"];
    const rows = filtradas.map((i) => [
      i.nombre,
      i.email,
      i.celular,
      i.taller.title,
      new Date(i.createdAt).toLocaleString("es-PA"),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inscripciones-talleres.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-brown-dark">Inscripciones a Talleres</h1>
          <p className="text-muted-foreground mt-1">Gestiona los inscritos en cada taller</p>
        </div>
        <Button onClick={exportarCSV} variant="outline" className="gap-2 self-start sm:self-auto">
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Users className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total inscritos</p>
              <p className="text-2xl font-bold">{inscripciones.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Talleres activos</p>
              <p className="text-2xl font-bold">{talleresUnicos.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o celular..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filtroTaller} onValueChange={setFiltroTaller}>
            <SelectTrigger className="sm:w-64">
              <SelectValue placeholder="Filtrar por taller" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los talleres</SelectItem>
              {talleresUnicos.map((t) => (
                <SelectItem key={t._id} value={t._id}>
                  {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-muted-foreground">
            {filtradas.length} inscripción{filtradas.length !== 1 ? "es" : ""}
            {filtroTaller !== "todos" && ` en "${talleresUnicos.find(t => t._id === filtroTaller)?.title}"`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtradas.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>{inscripciones.length === 0 ? "Aún no hay inscripciones" : "Sin resultados para tu búsqueda"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Taller</TableHead>
                    <TableHead>Fecha inscripción</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtradas.map((inscripcion) => (
                    <TableRow key={inscripcion._id}>
                      <TableCell className="font-medium">{inscripcion.nombre}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-sm">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <a href={`mailto:${inscripcion.email}`} className="hover:underline text-blue-600">
                              {inscripcion.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            {inscripcion.celular}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 font-medium">
                            🎨 {inscripcion.taller.title}
                          </Badge>
                          {inscripcion.taller.tallerInfo?.fechaEvento && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {inscripcion.taller.tallerInfo.fechaEvento}
                            </div>
                          )}
                          {inscripcion.taller.tallerInfo?.lugar && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {inscripcion.taller.tallerInfo.lugar}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(inscripcion.createdAt).toLocaleString("es-PA", {
                          year: "numeric", month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar inscripción?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se eliminará la inscripción de <strong>{inscripcion.nombre}</strong> al taller <strong>{inscripcion.taller.title}</strong>. Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive hover:bg-destructive/90"
                                onClick={() => deleteMutation.mutate(inscripcion._id)}
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTalleres;
